import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable, isDevMode, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, EMPTY } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

import { AuthConfigService } from '../auth-config/auth-config.service';
import { AuthService } from '../auth/auth.service';
import { AuthResponse, InteractionRequiredAuthError, ClientAuthError } from 'msal'; // Using MSAL v1 types
import { ConsentScopesService } from '../config/consent-scopes.service';

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
    constructor(
        private authConfigService: AuthConfigService,
        private injector: Injector,
        private consentScopesService: ConsentScopesService,
        private router: Router
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const apiEndpoint = this.authConfigService.config.apiEndpoint;

        // Get the audience from the endpoints map using the apiEndpoint as the key
        const endpointsMap = this.authConfigService.config.endpoints;
        const apiAudience = endpointsMap ? endpointsMap[apiEndpoint] : null;

        if (!apiAudience) {
            console.error(`AuthInterceptor: Could not find audience for endpoint ${apiEndpoint} in AuthConfigService endpoints map. Cannot construct API scope.`);
             // Let the request pass through without attempting to add a token
             return next.handle(request);
        }
        const resource = [`${apiAudience}/user_impersonation`];

        // Only intercept configured API requests
        if (apiEndpoint && resource && request.url.startsWith(apiEndpoint)) {
            // Get AuthService via injector inside the intercept method
            const authService = this.injector.get(AuthService);

            // Attempt to get token silently
            return authService.getBearerToken(resource).pipe(
                switchMap((tokenResponse: AuthResponse) => {
                    if (!tokenResponse || !tokenResponse.accessToken) { // Check for token explicitly
                        console.warn('AuthInterceptor: getBearerToken returned no access token. Passing request without token.');
                        return next.handle(request);
                    }
                    // Clone request and set header
                    const authorizedRequest = request.clone({
                        setHeaders: { // Use setHeaders
                            'Authorization': `Bearer ${tokenResponse.accessToken}`
                        }
                    });
                    return next.handle(authorizedRequest); // Pass the cloned request
                }),
                catchError((error: any) => {
                    // Check specifically for MSAL errors requiring interaction
                    if (error instanceof InteractionRequiredAuthError ||
                        error instanceof ClientAuthError ||
                        (error && error.name === 'InteractionRequiredAuthError') || // Check name for robustness
                        (error && error.name === 'ClientAuthError')) {

                        if (isDevMode()) {
                            console.warn('AuthInterceptor: Silent token acquisition failed (Interaction Required). Triggering login...', error);
                        }
                        // Interaction is required, initiate login redirect
                        authService.login();
                        // Return EMPTY to effectively cancel the original request and prevent further error propagation
                        return EMPTY;
                    } else {
                        // For other errors (network, API errors after getting token, etc.), log and re-throw
                        if (isDevMode()) {
                            console.error('AuthInterceptor: Non-interaction error during request/token handling:', error);
                        }
                        return throwError(() => error); // Re-throw other errors
                    }
                })
            );
        } else {
            // Pass through non-API requests
            return next.handle(request);
        }
    }
}
